import React, { useState } from 'react';
import { useFinanceStore } from '../api/useFinanceStore';
import { CreditCard, TrendingDown, Zap, Plus, X, Trash2, Edit2, Check, Ban } from 'lucide-react';
import { formatZAR } from '../../../utils/formatters';

const EXPENSE_CATEGORIES = ['Office Supplies', 'Salaries', 'Rent', 'Utilities', 'Travel', 'Marketing', 'Software', 'Professional Fees', 'Other'];

const ExpenseTracker: React.FC = () => {
    const { expenses, addExpense, updateExpense, deleteExpense } = useFinanceStore();
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [category, setCategory] = useState('');
    const [amount, setAmount] = useState('');
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [description, setDescription] = useState('');
    const [editCategory, setEditCategory] = useState('');
    const [editAmount, setEditAmount] = useState('');
    const [editDate, setEditDate] = useState('');
    const [editDescription, setEditDescription] = useState('');

    const handleAddExpense = () => {
        if (!category || !amount || Number(amount) <= 0) {return;}
        addExpense({
            category,
            amount: Number(amount),
            date,
            description: description || category,
        });
        setShowForm(false);
        setCategory('');
        setAmount('');
        setDate(new Date().toISOString().split('T')[0]);
        setDescription('');
    };

    const handleStartEdit = (expense: typeof expenses[0]) => {
        setEditingId(expense.id);
        setEditCategory(expense.category);
        setEditAmount(String(expense.amount));
        setEditDate(expense.date);
        setEditDescription(expense.description);
    };

    const handleSaveEdit = async (id: string) => {
        await updateExpense(id, {
            category: editCategory,
            amount: parseFloat(editAmount),
            date: editDate,
            description: editDescription,
        });
        setEditingId(null);
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-bold text-slate-800 uppercase tracking-tight">Expense Tracking</h3>
                <button
                    onClick={() => setShowForm(true)}
                    className="flex items-center gap-2 bg-slate-800 text-white px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-900 transition-all active:scale-95"
                >
                    <Plus size={14} /> Log Expense
                </button>
            </div>

            {/* New Expense Form */}
            {showForm && (
                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-3">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-black text-slate-800 uppercase tracking-widest">Log New Expense</div>
                        <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <select value={category} onChange={e => setCategory(e.target.value)} className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400 bg-white">
                            <option value="">Select Category *</option>
                            {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <input type="number" placeholder="Amount (ZAR) *" value={amount} onChange={e => setAmount(e.target.value)} className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400" />
                        <input type="date" value={date} onChange={e => setDate(e.target.value)} className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400" />
                        <input type="text" placeholder="Description" value={description} onChange={e => setDescription(e.target.value)} className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400" />
                    </div>
                    <button onClick={handleAddExpense} disabled={!category || !amount || Number(amount) <= 0} className="w-full py-2 bg-slate-800 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                        Log Expense
                    </button>
                </div>
            )}

            <div className="grid gap-4">
                {expenses.length === 0 ? (
                    <div className="p-10 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-300 text-slate-400 font-medium">
                        No expenses recorded. Log your first expense to get started.
                    </div>
                ) : (
                    expenses.map(expense => (
                        <div key={expense.id} className="bg-white rounded-2xl shadow-sm border border-slate-200 group hover:border-blue-300 transition-all overflow-hidden">
                            {editingId === expense.id ? (
                                <div className="p-4 space-y-3">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <select value={editCategory} onChange={e => setEditCategory(e.target.value)} className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400 bg-white">
                                            {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                        <input type="number" value={editAmount} onChange={e => setEditAmount(e.target.value)} className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400" />
                                        <input type="date" value={editDate} onChange={e => setEditDate(e.target.value)} className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400" />
                                        <input type="text" value={editDescription} onChange={e => setEditDescription(e.target.value)} placeholder="Description" className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400" />
                                    </div>
                                    <div className="flex gap-2 justify-end">
                                        <button onClick={() => handleSaveEdit(expense.id)} className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded-lg text-xs font-bold hover:bg-green-700 transition-all"><Check size={14} /> Save</button>
                                        <button onClick={() => setEditingId(null)} className="flex items-center gap-1 px-3 py-1 bg-slate-200 text-slate-700 rounded-lg text-xs font-bold hover:bg-slate-300 transition-all"><Ban size={14} /> Cancel</button>
                                    </div>
                                </div>
                            ) : (
                                <div className="p-4 flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <div className={`p-3 rounded-xl ${expense.optimized ? 'bg-green-100 text-green-600' : 'bg-amber-100 text-amber-600'}`}>
                                            {expense.optimized ? <Zap size={20} /> : <CreditCard size={20} />}
                                        </div>
                                        <div className="text-sm">
                                            <div className="font-bold text-slate-800">{formatZAR(expense.amount)} <span className="text-slate-400 font-normal ml-2">• {expense.category}</span></div>
                                            <div className="text-[10px] text-slate-400 uppercase font-black tracking-widest">{expense.date} • {expense.description}</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {expense.optimized ? (
                                            <span className="px-2 py-1 bg-green-50 text-green-600 rounded-lg text-[9px] font-black uppercase tracking-wider border border-green-100">Optimized</span>
                                        ) : (
                                            <button className="px-2 py-1 bg-amber-50 text-amber-600 rounded-lg text-[9px] font-black uppercase tracking-wider border border-amber-100 hover:bg-amber-100 transition-all">
                                                Optimize
                                            </button>
                                        )}
                                        <button
                                            onClick={() => handleStartEdit(expense)}
                                            className="p-2 text-slate-400 hover:text-blue-600 transition-colors"
                                            title="Edit"
                                        >
                                            <Edit2 size={14} />
                                        </button>
                                        <button
                                            onClick={() => deleteExpense(expense.id)}
                                            className="p-2 text-slate-400 hover:text-red-500 transition-colors"
                                            title="Delete"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default ExpenseTracker;
